import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Star, Clock, Users, ArrowRight, BookOpen, Code, TestTube, Globe, Smartphone, Cloud, Settings } from 'lucide-react';
import { usePromotional } from '../contexts/PromotionalContext';
import CouponBanner from '../components/ui/CouponBanner';
import PromotionalBanner from '../components/ui/PromotionalBanner';

const AutomationPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const { config, isActive } = usePromotional();

  const categories = [
    { id: 'all', name: 'All Courses' },
    { id: 'playwright', name: 'Playwright' },
    { id: 'selenium', name: 'Selenium' },
    { id: 'api-testing', name: 'API Testing' },
    { id: 'mobile', name: 'Mobile Testing' },
    { id: 'microservices', name: 'Microservices' },
    { id: 'cicd', name: 'CI/CD' },
    { id: 'frameworks', name: 'Framework Development' },
  ];

  const levels = [
    { id: 'all', name: 'All Levels' },
    { id: 'beginner', name: 'Beginner' },
    { id: 'intermediate', name: 'Intermediate' },
    { id: 'advanced', name: 'Advanced' },
  ];

  const courses = [
    {
      id: 1,
      title: 'Automation Framework Development with Playwright in C# .NET',
      description: 'Master building robust, scalable test automation frameworks using Playwright with C# .NET. Learn advanced framework architecture, design patterns, reporting, and CI/CD integration.',
      longDescription: 'Build enterprise-grade automation frameworks from scratch using Playwright and C# .NET. Cover advanced topics like Page Object Model, data-driven testing, parallel execution, custom reporting, and CI/CD pipeline integration.',
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=240&fit=crop',
      price: 'Enroll on Udemy',
      rating: 4.7,
      students: 3200,
      category: 'playwright',
      level: 'advanced',
      duration: '18 hours',
      technologies: ['Playwright', 'C#', '.NET', 'NUnit', 'SpecFlow'],
      skills: ['Framework Architecture', 'Page Object Model', 'Data-Driven Testing', 'CI/CD Integration', 'Custom Reporting'],
      udemyUrl: 'https://www.udemy.com/course/framework-development-with-playwright-dotnet/',
      whatYoullLearn: [
        'Build scalable Playwright automation frameworks',
        'Implement advanced design patterns',
        'Create robust reporting mechanisms',
        'Integrate with CI/CD pipelines',
        'Handle complex test scenarios'
      ]
    },
    {
      id: 2,
      title: 'API Testing with RestSharp along with Framework Development',
      description: 'Comprehensive API testing using RestSharp in C#. Learn to build API automation frameworks, validate REST services, handle authentication, and integrate with BDD using SpecFlow.',
      longDescription: 'Master API testing and automation framework development using RestSharp and C#. Cover REST API fundamentals, authentication mechanisms, data validation, and BDD approach with SpecFlow.',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=240&fit=crop',
      price: 'Enroll on Udemy',
      rating: 4.6,
      students: 2800,
      category: 'api-testing',
      level: 'intermediate',
      duration: '15 hours',
      technologies: ['RestSharp', 'C#', 'SpecFlow', 'JSON', 'XML'],
      skills: ['API Testing', 'REST Services', 'Authentication', 'BDD Testing', 'Data Validation'],
      udemyUrl: 'https://www.udemy.com/course/api-testing-with-restsharp-and-specflow-in-csharp/',
      whatYoullLearn: [
        'Master RestSharp for API automation',
        'Build comprehensive API test frameworks',
        'Handle various authentication methods',
        'Implement BDD with SpecFlow',
        'Validate complex API responses'
      ]
    },
    {
      id: 3,
      title: 'End to End Test Automation with Playwright (TS/C#/Java)',
      description: 'Complete guide to end-to-end test automation using Playwright across multiple programming languages. Master web testing, mobile testing, and cross-browser automation.',
      longDescription: 'Comprehensive course covering Playwright automation across TypeScript, C#, and Java. Learn web automation, mobile testing, API testing, visual testing, and advanced Playwright features.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=240&fit=crop',
      price: 'Enroll on Udemy',
      rating: 4.8,
      students: 4500,
      category: 'playwright',
      level: 'intermediate',
      duration: '22 hours',
      technologies: ['Playwright', 'TypeScript', 'C#', 'Java', 'Node.js'],
      skills: ['Cross-Browser Testing', 'Mobile Testing', 'Visual Testing', 'API Integration', 'Multi-Language Support'],
      udemyUrl: 'https://www.udemy.com/course/e2e-playwright/',
      whatYoullLearn: [
        'Master Playwright in multiple languages',
        'Implement cross-browser testing strategies',
        'Build mobile testing solutions',
        'Create visual regression tests',
        'Integrate API and UI testing'
      ]
    },
    {
      id: 4,
      title: 'Event-Driven Microservice: Build and Test Apps with C# .NET',
      description: 'Learn to build and test event-driven microservices using C# .NET. Master microservice architecture, event sourcing, message queues, and comprehensive testing strategies.',
      longDescription: 'Deep dive into event-driven microservice architecture with C# .NET. Cover event sourcing, CQRS patterns, message brokers, service communication, and testing methodologies for distributed systems.',
      image: 'https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=400&h=240&fit=crop',
      price: 'Enroll on Udemy',
      rating: 4.7,
      students: 2100,
      category: 'microservices',
      level: 'advanced',
      duration: '20 hours',
      technologies: ['C#', '.NET Core', 'RabbitMQ', 'Docker', 'Kubernetes'],
      skills: ['Microservice Architecture', 'Event Sourcing', 'Message Queues', 'Integration Testing', 'Contract Testing'],
      udemyUrl: 'https://www.udemy.com/course/testing-eda-microservices/',
      whatYoullLearn: [
        'Build event-driven microservices',
        'Implement event sourcing patterns',
        'Master microservice testing strategies',
        'Handle service-to-service communication',
        'Deploy and monitor distributed systems'
      ]
    },
    {
      id: 5,
      title: 'Automation Framework Development with Selenium C# .NET',
      description: 'Advanced Selenium automation framework development using C# .NET. Build enterprise-ready frameworks with design patterns, parallel execution, and comprehensive reporting.',
      longDescription: 'Master advanced Selenium automation framework development with C# .NET. Cover framework architecture, design patterns, data management, parallel execution, custom reporting, and CI/CD integration.',
      image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=240&fit=crop',
      price: 'Enroll on Udemy',
      rating: 4.5,
      students: 5200,
      category: 'selenium',
      level: 'advanced',
      duration: '16 hours',
      technologies: ['Selenium', 'C#', '.NET', 'NUnit', 'ExtentReports'],
      skills: ['Framework Design', 'Page Factory Pattern', 'Data-Driven Testing', 'Parallel Execution', 'Custom Reporting'],
      udemyUrl: 'https://www.udemy.com/course/framework-development-with-selenium-csharp-advanced/',
      whatYoullLearn: [
        'Design scalable Selenium frameworks',
        'Implement advanced design patterns',
        'Master parallel test execution',
        'Create comprehensive test reports',
        'Integrate with build pipelines'
      ]
    },
    {
      id: 6,
      title: 'Advanced Automation Testing of Modern Apps with C# .NET',
      description: 'Modern application testing strategies using C# .NET. Learn to test SPAs, PWAs, microservices, and cloud-native applications with advanced automation techniques.',
      longDescription: 'Comprehensive course on testing modern applications including SPAs, PWAs, microservices, and cloud-native apps. Cover advanced testing strategies, containerized testing, and modern CI/CD practices.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=240&fit=crop',
      price: 'Enroll on Udemy',
      rating: 4.6,
      students: 1800,
      category: 'frameworks',
      level: 'advanced',
      duration: '19 hours',
      technologies: ['C#', '.NET 6', 'Docker', 'Kubernetes', 'Azure'],
      skills: ['Modern App Testing', 'Containerized Testing', 'Cloud Testing', 'Performance Testing', 'Security Testing'],
      udemyUrl: 'https://www.udemy.com/course/modern-dotnet-testing/',
      whatYoullLearn: [
        'Test modern application architectures',
        'Implement containerized testing',
        'Master cloud-native testing strategies',
        'Performance and security testing',
        'Advanced CI/CD integration'
      ]
    },
    {
      id: 7,
      title: 'Spring Boot for Automation Testing-UI and Microservice',
      description: 'Comprehensive Spring Boot testing covering UI automation, microservice testing, and integration strategies. Learn to build robust testing solutions in Java ecosystem.',
      longDescription: 'Master testing strategies for Spring Boot applications including UI automation, microservice testing, integration testing, and test containers. Cover Spring Test framework and advanced testing patterns.',
      image: 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?w=400&h=240&fit=crop',
      price: 'Enroll on Udemy',
      rating: 4.4,
      students: 2600,
      category: 'microservices',
      level: 'intermediate',
      duration: '17 hours',
      technologies: ['Spring Boot', 'Java', 'JUnit', 'Mockito', 'TestContainers'],
      skills: ['Spring Testing', 'Integration Testing', 'Mock Testing', 'Test Containers', 'Microservice Testing'],
      udemyUrl: 'https://www.udemy.com/course/spring-boot-testing/',
      whatYoullLearn: [
        'Master Spring Boot testing framework',
        'Implement comprehensive integration tests',
        'Use TestContainers for isolated testing',
        'Build microservice testing strategies',
        'Advanced mocking and stubbing techniques'
      ]
    },
    {
      id: 8,
      title: 'Build+Deploy+Test with Jenkins 2.0',
      description: 'Complete CI/CD pipeline development with Jenkins 2.0. Learn to build, deploy, and test applications with advanced pipeline strategies and automation integration.',
      longDescription: 'Master Jenkins 2.0 for complete CI/CD pipeline development. Cover pipeline as code, automated testing integration, deployment strategies, and advanced Jenkins features for enterprise environments.',
      image: 'https://images.unsplash.com/photo-1618477247222-acbdb0e159b3?w=400&h=240&fit=crop',
      price: 'Enroll on Udemy',
      rating: 4.3,
      students: 3400,
      category: 'cicd',
      level: 'intermediate',
      duration: '14 hours',
      technologies: ['Jenkins', 'Docker', 'Kubernetes', 'Git', 'Pipeline'],
      skills: ['CI/CD Pipelines', 'Infrastructure as Code', 'Automated Deployment', 'Pipeline Security', 'Monitoring'],
      udemyUrl: 'https://www.udemy.com/course/working-with-jenkins/',
      whatYoullLearn: [
        'Build complete CI/CD pipelines',
        'Implement pipeline as code',
        'Master automated testing integration',
        'Deploy applications automatically',
        'Monitor and optimize pipelines'
      ]
    },
    {
      id: 9,
      title: 'Automation Framework with Selenium Java (Advanced)',
      description: 'Advanced Selenium automation framework development using Java. Master enterprise-level framework architecture, design patterns, and advanced automation techniques.',
      longDescription: 'Build enterprise-grade Selenium automation frameworks with Java. Cover advanced framework design, TestNG integration, data management, parallel execution, and comprehensive reporting solutions.',
      image: 'https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=400&h=240&fit=crop',
      price: 'Enroll on Udemy',
      rating: 4.5,
      students: 6700,
      category: 'selenium',
      level: 'advanced',
      duration: '20 hours',
      technologies: ['Selenium', 'Java', 'TestNG', 'Maven', 'ExtentReports'],
      skills: ['Java Framework Design', 'TestNG Integration', 'Maven Build', 'Advanced Reporting', 'Parallel Testing'],
      udemyUrl: 'https://www.udemy.com/course/automation-framework-with-selenium/',
      whatYoullLearn: [
        'Design enterprise Selenium frameworks',
        'Master TestNG for test management',
        'Implement advanced reporting solutions',
        'Build reusable automation components',
        'Optimize framework performance'
      ]
    },
    {
      id: 10,
      title: 'Understand Docker and Using it for Selenium Automation',
      description: 'Comprehensive guide to containerized Selenium automation using Docker. Learn to build scalable, portable automation solutions with Docker containers and orchestration.',
      longDescription: 'Master Docker for Selenium automation including containerization, scaling, and orchestration. Cover Docker Compose, Selenium Grid in containers, and cloud deployment strategies.',
      image: 'https://images.unsplash.com/photo-1605745341112-85968b19335b?w=400&h=240&fit=crop',
      price: 'Enroll on Udemy',
      rating: 4.4,
      students: 2900,
      category: 'selenium',
      level: 'intermediate',
      duration: '12 hours',
      technologies: ['Docker', 'Selenium', 'Docker Compose', 'Kubernetes', 'Selenium Grid'],
      skills: ['Containerization', 'Selenium Grid', 'Docker Orchestration', 'Scalable Testing', 'Cloud Deployment'],
      udemyUrl: 'https://www.udemy.com/course/understanding-docker-performing-selenium-automation/',
      whatYoullLearn: [
        'Containerize Selenium automation',
        'Build scalable Selenium Grid',
        'Master Docker orchestration',
        'Deploy tests in cloud environments',
        'Optimize containerized testing performance'
      ]
    },
    {
      id: 11,
      title: 'Advanced Automation Framework with Appium C#',
      description: 'Build advanced mobile automation frameworks using Appium and C#. Master cross-platform mobile testing, device management, and enterprise mobile testing strategies.',
      longDescription: 'Comprehensive mobile automation framework development with Appium and C#. Cover iOS and Android testing, device farms, cloud testing, and advanced mobile testing patterns.',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=240&fit=crop',
      price: 'Enroll on Udemy',
      rating: 4.3,
      students: 1600,
      category: 'mobile',
      level: 'advanced',
      duration: '16 hours',
      technologies: ['Appium', 'C#', '.NET', 'Android', 'iOS'],
      skills: ['Mobile Framework Design', 'Cross-Platform Testing', 'Device Management', 'Cloud Testing', 'Mobile CI/CD'],
      udemyUrl: 'https://www.udemy.com/course/automation-framework-development-with-appium/',
      whatYoullLearn: [
        'Build advanced Appium frameworks',
        'Master cross-platform mobile testing',
        'Implement device management strategies',
        'Integrate with cloud testing platforms',
        'Advanced mobile testing patterns'
      ]
    },
    {
      id: 12,
      title: 'Selenium with C# .NET for Absolute Beginners',
      description: 'Complete beginner-friendly course on Selenium automation using C# .NET. Learn web automation fundamentals, basic framework concepts, and essential testing practices.',
      longDescription: 'Perfect starting point for Selenium automation with C# .NET. Cover automation basics, web element interaction, basic framework concepts, and fundamental testing practices.',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=240&fit=crop',
      price: 'Enroll on Udemy',
      rating: 4.6,
      students: 4200,
      category: 'selenium',
      level: 'beginner',
      duration: '10 hours',
      technologies: ['Selenium', 'C#', '.NET', 'Visual Studio', 'MSTest'],
      skills: ['Selenium Basics', 'Web Element Interaction', 'Basic Framework', 'Test Management', 'Debugging'],
      udemyUrl: 'https://www.udemy.com/course/selenium-dotnet-basics/',
      whatYoullLearn: [
        'Selenium automation fundamentals',
        'Web element identification and interaction',
        'Basic test framework setup',
        'Essential debugging techniques',
        'Best practices for beginners'
      ]
    }
  ];

  const filteredCourses = courses.filter((course) => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.technologies.some(tech => tech.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory;
    const matchesLevel = selectedLevel === 'all' || course.level === selectedLevel;
    return matchesSearch && matchesCategory && matchesLevel;
  });

  const getIcon = (category: string) => {
    switch (category) {
      case 'playwright': return Globe;
      case 'selenium': return TestTube;
      case 'api-testing': return Code;
      case 'mobile': return Smartphone;
      case 'microservices': return Cloud;
      case 'cicd': return Settings;
      default: return BookOpen;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
          Test Automation <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Courses</span>
        </h1>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Master modern test automation with comprehensive courses covering Selenium, Playwright, API testing, mobile automation, 
          microservices testing, and CI/CD integration. Build enterprise-ready automation frameworks and advance your testing career.
        </p>
      </div>

      {/* Promotional Banner */}
      <div className="mb-8">
        <PromotionalBanner 
          variant="inline"
          className="max-w-4xl mx-auto"
        />
      </div>

      {/* Coupon Banner */}
      {config.isEnabled && isActive && (
        <div className="flex justify-center mb-8">
          <div className="max-w-2xl w-full px-4">
            <CouponBanner
              code={config.couponCode}
              description={config.description}
              variant="banner"
              className="shadow-lg hover:shadow-xl transition-shadow duration-300"
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-8 space-y-4 lg:space-y-0 lg:flex lg:items-center lg:justify-between lg:gap-6">
        {/* Search */}
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search courses, technologies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm hover:border-blue-300 transition-colors"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Category Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm hover:border-blue-300 transition-colors min-w-[150px]"
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Level Filter */}
          <div className="flex items-center space-x-2">
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm hover:border-blue-300 transition-colors min-w-[130px]"
            >
              {levels.map((level) => (
                <option key={level.id} value={level.id}>
                  {level.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-6">
        <p className="text-gray-600">
          Showing {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Courses Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {filteredCourses.map((course) => {
          const CategoryIcon = getIcon(course.category);
          
          return (
            <div
              key={course.id}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 transform cursor-pointer group"
            >
              <img
                src={course.image}
                alt={course.title}
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <CategoryIcon className="w-5 h-5 text-blue-600" />
                    <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                      course.level === 'beginner' ? 'text-green-700 bg-green-100' :
                      course.level === 'intermediate' ? 'text-blue-700 bg-blue-100' :
                      'text-purple-700 bg-purple-100'
                    }`}>
                      {course.level.charAt(0).toUpperCase() + course.level.slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    {course.duration}
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors leading-tight">
                  {course.title}
                </h3>
                
                <p className="text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                  {course.description}
                </p>

                {/* Technologies */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Technologies:</h4>
                  <div className="flex flex-wrap gap-1">
                    {course.technologies.slice(0, 4).map((tech, index) => (
                      <span 
                        key={index}
                        className="px-2 py-1 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-700"
                      >
                        {tech}
                      </span>
                    ))}
                    {course.technologies.length > 4 && (
                      <span className="px-2 py-1 bg-gray-100 rounded-md text-xs text-gray-600">
                        +{course.technologies.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-yellow-400" />
                    {course.rating}
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {course.students.toLocaleString()}+ students
                  </div>
                </div>

                {/* Coupon Code Display */}
                {config.isEnabled && isActive && (
                  <div className="mb-4">
                    <CouponBanner
                      code={config.couponCode}
                      description={config.description}
                      variant="card"
                      showCopy={false}
                      className="text-xs"
                    />
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    {course.price}
                  </span>
                  <a
                    href={course.udemyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group/btn bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105 font-medium flex items-center gap-2"
                  >
                    <BookOpen className="w-4 h-4" />
                    View on Udemy
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* No Results */}
      {filteredCourses.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Search className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No courses found
          </h3>
          <p className="text-gray-600">
            Try adjusting your search terms or filters to find what you're looking for.
          </p>
        </div>
      )}

      {/* Learning Path Section */}
      <div className="mt-16 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-100">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Learn More on AI Courses
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Discover how AI is transforming automation testing and enhance your skills with our 
            comprehensive AI & Testing learning paths designed for modern test engineers.
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/paths"
            className="bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white px-6 py-3 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
          >
            <BookOpen className="w-5 h-5" />
            Explore Learning Path
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/course-bundles"
            className="bg-white border-2 border-blue-300 text-blue-600 hover:border-blue-500 hover:text-blue-700 px-6 py-3 font-semibold rounded-xl transition-colors duration-300 flex items-center justify-center gap-2"
          >
            View Course Bundles
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AutomationPage;